// ── 상황판 어셈블리 (2026-06-07 통합 — UnifiedCard) ─────────────────────────
import React, { useMemo, useState } from 'react';
import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/utils';
import { useIsMobile, fmtTime } from './utils';
import { DetailModal } from './DetailModal';
import { useSituationBoard } from './useSituationBoard';
import { useSituationStats } from './useSituationStats';
import { SbContent } from './SbContent';
import { useSituationWidgets } from './useSituationWidgets';
import { useMyPersonalWidgets } from './useMyPersonalWidgets';
import { useAdminWidgets } from './useAdminWidgets';
import { useWidgetSummaries } from './useWidgetSummaries';
import { SituationWidgetSettingsPanel } from './SituationWidgetSettingsPanel';
import { STD_HEADER_TOP } from '../adminlayout/designStandard';

/** 관리자 상황판 메인 페이지 */
export default function SituationBoard() {
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {
    loading: widgetsLoading,
    error: widgetsError,
    widgets,
    visibleCodes,
    hiddenSet,
    accentMap,
    orderUnified,
    widgetMenuMap,
    noAccessCodes,
    visitCategories,
    toggleHidden,
    setAccentForCode,
    setWidgetOrder,
  } = useSituationWidgets({ showToast });
  const canFetch = !widgetsLoading && !widgetsError && visibleCodes.length > 0;
  // 위젯 메뉴가 없는 손님 테넌트 직원 — 그 위젯은 원래 데이터 주소 대신 카드 요약만(2026-10-24, useWidgetSummaries)
  const summaries = useWidgetSummaries({ widgets, visibleCodes, enabled: canFetch, showToast });
  const dataCodes = useMemo(() => visibleCodes.filter((c) => !summaries.summarySet.has(c)), [visibleCodes, summaries.summarySet]);

  const {
    snapshot, loading, lastUpdated,
    detail, setDetail,
    fetchSnapshot,
    handlers,
  } = useSituationBoard({ showToast, widgetCodes: dataCodes, enabled: canFetch });

  // 직원 본인 위젯 (출퇴근/연차/일일콜/정산) — 자체 fetch
  const personal = useMyPersonalWidgets({ visibleCodes: dataCodes, showToast, enabled: canFetch });
  // 관리자 메뉴 위젯 (admin_* 60+개) — batch fetch
  const admin = useAdminWidgets({ visibleCodes: dataCodes, enabled: canFetch });

  const stats = summaries.applyStats(useSituationStats(snapshot));

  const fmtUpdated = lastUpdated ? fmtTime(lastUpdated) : null;
  const m = isMobile;

  return (
    <div className={cn('max-w-[1400px]', m ? 'pb-16' : '')}>
      <DetailModal detail={detail} onClose={() => setDetail(null)} isMobile={m} />

      {/* 헤더 — 통일 기준(직원 관리) 반영: 두께 700, 데스크톱 시작 높이 STD_HEADER_TOP (2026-09-28) */}
      <div className={cn('flex justify-between items-end mb-4', m ? 'pt-1' : '')} style={m ? undefined : { marginTop: STD_HEADER_TOP }}>
        <div>
          <h2 className={cn('m-0 font-bold text-[var(--text)] tracking-tight uppercase', m ? 'text-2xl' : 'text-[26px]')}>Dashboard</h2>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[12px] text-[var(--text-muted)]">
            <span>상황판</span>
            <span>· {stats.today}</span>
            {fmtUpdated && <span>· 업데이트 {fmtUpdated}</span>}
            {loading && <span className="text-accent-500 animate-pulse">불러오는 중...</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(true)}
            className={cn(
              'border border-slate-900 dark:border-accent-600 rounded-xl font-bold text-xs text-white',
              'bg-slate-900 hover:bg-slate-700 dark:bg-accent-600 dark:hover:bg-accent-500 transition-colors cursor-pointer',
              m ? 'px-3 py-2.5' : 'px-3.5 py-2'
            )}
          >
            + 위젯
          </button>
          <button
            onClick={() => { fetchSnapshot(); summaries.reload(); }}
            disabled={loading || !canFetch}
            className={cn(
              'border border-[var(--border)] rounded-xl font-bold text-xs text-[var(--text-sub)]',
              'bg-[var(--bg-card)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-shadow cursor-pointer',
              m ? 'px-3.5 py-2.5' : 'px-3.5 py-2',
              (loading || !canFetch) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {loading ? '...' : '새로고침'}
          </button>
        </div>
      </div>

      {/* 기능 카드 런처는 /admin/home 으로 분리 (2026-08-26 A안) — 상황판은 위젯 전용 모니터링 */}
      <SituationWidgetSettingsPanel
        open={settingsOpen}
        widgets={widgets}
        hiddenSet={hiddenSet}
        accentMap={accentMap}
        onToggleHidden={toggleHidden}
        onChangeAccent={setAccentForCode}
        onClose={() => setSettingsOpen(false)}
      />

      <div className={cn('grid gap-3', m ? 'px-0' : '')}>
        {widgetsLoading && (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-5 text-sm font-bold text-[var(--text-sub)]">
            상황판 위젯을 불러오는 중입니다.
          </div>
        )}

        {!widgetsLoading && widgetsError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm font-bold text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {widgetsError}
          </div>
        )}

        {!widgetsLoading && !widgetsError && visibleCodes.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-card)] p-6 text-sm text-[var(--text-sub)]">
            표시할 위젯이 없습니다. 위젯 버튼에서 숨김을 해제하거나 관리자에게 업무/기능 배정을 확인해 주세요.
          </div>
        )}

        {/* 본문 — PC/모바일 분기 제거, SbContent 한 컴포넌트 */}
        {canFetch && (
          <SbContent
            stats={stats}
            handlers={summaries.wrapHandlers(handlers)}
            visibleWidgetCodes={visibleCodes}
            personal={personal}
            admin={admin}
            accentMap={accentMap}
            orderUnified={orderUnified}
            setWidgetOrder={setWidgetOrder}
            isMobile={m}
            widgetMenuMap={widgetMenuMap}
            noAccessCodes={noAccessCodes}
            summaryCodes={summaries.summarySet}
            visitCategories={visitCategories}
          />
        )}
      </div>
    </div>
  );
}
